<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'MCreateFixedAsset',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    assetType: {
      type: String,
      required: false,
      default: 'general',
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const mainForm = ref(null);

  const $refs = {
    get mainForm() {
      return mainForm.value;
    },
  };

  const formName = ref('rental');
  const formId = ref(null);
  const item = ref(null);
  const fixedAssetTypes = ref([
    {
      id: 'general',
      name: $fn.value.pgettext('FixedAssetTypes', 'General'),
    },
    {
      id: 'waste_bin',
      name: $fn.value.pgettext('FixedAssetTypes', 'Waste Bin'),
    },
  ]);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'md',
  }));

  const $data = computed(() => ({
    formName: formName.value,
    formId: formId.value,
    item: item.value,
    fixedAssetTypes: fixedAssetTypes.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_formId = () => {
    return $fn.value.randomElementId();
  };
  const $$load_item = () => {
    return $fn.value.schemaDefaults('CreateFixedAsset', {
      asset_type: props.assetType,
      waste_bin: {
        bin_type: null,
        bin_number: null,
        capacity: {
          amount: null,
          unit: 'liter',
        },
      },
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'formName' },
      { name: 'formId' },
      { name: 'item' },
      { name: 'fixedAssetTypes' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: { formId: $$load_formId, item: $$load_item },
    ux: {},
    dataRefs: {
      formName,
      formId,
      item,
      fixedAssetTypes,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return { title: $fn.value.gettext('Create Fixed Asset') };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.assetType,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('assetType');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_mpnvc1owc_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeModal_jg46zdfiz_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
      },
    };
  };
  const act_1040167445267876_navigate_ee9kzlnp3_getActionArgs = async (
    $event
  ) => {
    return {
      location: {
        name: 'app.fixedAsset',
        params: {
          id: $event.data.response.data.id,
        },
      },
    };
  };
  const act_1040167445267876_reloadGrid_yr0nsk6vk_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'fixedAssets',
    };
  };
  const act_1040167445267876_reloadGrid_c03kc2w24_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'wasteBins',
    };
  };
  const act_1040167445267876_request_7lq1qxv6q_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'create_fixed_asset',
      data: item.value,
    };
  };

  const act_7315092382398562_closeModal_mpnvc1owc =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_mpnvc1owc_getActionArgs,
    });
  const inst_evh_7315092382398562_close_z0vwq6ajh =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_mpnvc1owc,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_z0vwq6ajh],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_qknw2w72m =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_diggwv3pz =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_qknw2w72m,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_diggwv3pz],
    event: 'reload',
  });
  const act_8564662037462133_closeModal_hc9mwewww =
    new actionTypes.CloseModalAction({});
  const inst_evh_8564662037462133_close_7cpxqbm5x =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeModal_hc9mwewww,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_7cpxqbm5x],
    event: 'close',
  });
  const act_1040167445267876_closeModal_jg46zdfiz =
    new actionTypes.CloseModalAction({
      actionArgs: act_1040167445267876_closeModal_jg46zdfiz_getActionArgs,
    });
  const inst_evh_1040167445267876_success_qw8ypix1l =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeModal_jg46zdfiz,
      event: 'success',
      displayName: 'closeModal',
    });
  const act_1040167445267876_navigate_ee9kzlnp3 =
    new actionTypes.NavigateAction({
      actionArgs: act_1040167445267876_navigate_ee9kzlnp3_getActionArgs,
    });
  const inst_evh_1040167445267876_success_y8iwrsvqq =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_navigate_ee9kzlnp3,
      event: 'success',
      displayName: 'navigate',
    });
  const act_1040167445267876_reloadGrid_yr0nsk6vk =
    new actionTypes.ReloadGridAction({
      actionArgs: act_1040167445267876_reloadGrid_yr0nsk6vk_getActionArgs,
    });
  const inst_evh_1040167445267876_success_5wbyipb6j =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_reloadGrid_yr0nsk6vk,
      event: 'success',
      displayName: 'reloadGrid',
    });
  const act_1040167445267876_reloadGrid_c03kc2w24 =
    new actionTypes.ReloadGridAction({
      actionArgs: act_1040167445267876_reloadGrid_c03kc2w24_getActionArgs,
    });
  const inst_evh_1040167445267876_success_8j7l19skc =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_reloadGrid_c03kc2w24,
      event: 'success',
      displayName: 'reloadGrid',
    });
  const act_1040167445267876_request_7lq1qxv6q = new actionTypes.RequestAction({
    actionArgs: act_1040167445267876_request_7lq1qxv6q_getActionArgs,
    displayName: 'create',
    events: [
      inst_evh_1040167445267876_success_qw8ypix1l,
      inst_evh_1040167445267876_success_y8iwrsvqq,
      inst_evh_1040167445267876_success_5wbyipb6j,
      inst_evh_1040167445267876_success_8j7l19skc,
    ],
  });
  const inst_evh_1040167445267876_submit_hkcfraa1u =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_request_7lq1qxv6q,
      event: 'submit',
      displayName: 'create',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_hkcfraa1u],
    event: 'submit',
  });
  const act_2434514588044777_closeComponent_4p792ri2i =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2434514588044777_clickPreventStop_pdh383kvk =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeComponent_4p792ri2i,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_pdh383kvk],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_z0vwq6ajh,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_diggwv3pz,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_7cpxqbm5x,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_success_qw8ypix1l,
    inst_evh_1040167445267876_success_y8iwrsvqq,
    inst_evh_1040167445267876_success_5wbyipb6j,
    inst_evh_1040167445267876_success_8j7l19skc,
    inst_evh_1040167445267876_submit_hkcfraa1u,
    create: inst_evh_1040167445267876_submit_hkcfraa1u,
    inst_evh_1040167445267876_submit,
    inst_evh_2434514588044777_clickPreventStop_pdh383kvk,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_z0vwq6ajh = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_z0vwq6ajh.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_diggwv3pz = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_diggwv3pz.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_7cpxqbm5x = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_7cpxqbm5x.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_qw8ypix1l = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_qw8ypix1l.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_y8iwrsvqq = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_y8iwrsvqq.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_5wbyipb6j = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_5wbyipb6j.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_8j7l19skc = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_8j7l19skc.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_hkcfraa1u = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_hkcfraa1u.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_pdh383kvk = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_pdh383kvk.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="MCreateFixedAsset"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :name="formName"
        schema="CreateFixedAsset"
        :id="formId"
        ref="mainForm"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <FieldsetFixedAssetGeneral :fixedAsset="item">
        </FieldsetFixedAssetGeneral>
        <hr v-if="assetType === 'waste_bin'" />
        <FieldsetFixedAssetWasteBin
          :fixedAsset="item"
          v-if="assetType === 'waste_bin'"
        >
        </FieldsetFixedAssetWasteBin>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :text="pgettext('Button', 'Save')"
          type="submit"
          :form="formId"
          variant="primary"
          icon="fa-save"
          :spinning="actions.create && actions.create.isRunning"
          :disabled="!forms[formName] || !forms[formName].submittable"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          :disabled="actions.create && actions.create.isRunning"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
